import { Component, Input, NgZone, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { IskillApiService } from '../../services/iskill-api.service';
import { ProductivityService, ProductivityRecord } from '@app/pages/productivity/productivity.service';
import { TeamManagementService } from '@app/pages/team-management/team-management.service';

/* ── Aggregate payload (mirrors iSkill Employee360Dto) ─────────────────── */
interface Identity {
  employeeId: string; name: string; jobTitle: string; poiType: string;
  careerLevel: string | null; city: string; country: string;
  joined: string | null; tenureYears: number | null;
  managerName: string | null; l3Name: string | null;
}
interface Employee360 {
  identity: Identity;
  productFit: { avgPct: number | null; productCount: number };
  sixBox: { boxLabel: string | null; boxNumber: number | null; status: string; calibratedAt: string | null };
  tScore: { current: number; projected: number | null; benchmark: number; appliedTargetCount: number; trend: { date: string; score: number }[] } | null;
  attrition: { risk: string | null; fungibleSkills: boolean | null } | null;
  aiUsage: {
    utilizedAic: number; actualSpend: number | null;
    overusedAic: number; excessSpend: number | null;
    monthlyCapUsd: number; modelCount: number;
    topModels: { model: string; utilizedAic: number; sharePct: number | null }[];
    asOf: string | null;
  } | null;
  copilot: {
    activityStatus: string | null;
    daysLast30: number; daysPrev30: number; daysPrev60: number;
    asOf: string | null;
  } | null;
  agents: {
    usingAgent: boolean; agile: boolean;
    agentCount: number; agents: string[];
    asOf: string | null;
  } | null;
  rnr: {
    awardsWon: number; nominations: number; pending: number;
    thisYearAwards: number;
    lastAwardCategory: string | null; lastAwardDate: string | null;
    categories: { name: string; count: number }[];
  } | null;
  certifications: { activeCount: number; expiringSoonCount: number; nextExpiryDays: number | null; nextExpiryName: string | null };
  competencies: {
    current: { id: string; name: string; level: string | null }[];
    movement: { name: string; from: string | null; to: string | null; direction: number; changedAt: string | null }[];
    since: string | null;
  };
  succession: { topTalent: boolean; criticalTalent: boolean; successorRequired: boolean | null; successorIdentified: boolean | null; successorReadiness: string | null } | null;
  recommendations: { kind: string; title: string; reason: string; cta: string; route: string }[];
}

/* ── Chart view-models ─────────────────────────────────────────────────── */
interface MetricChart {
  metricId: string; metricName: string; latest: string;
  linePoints: string; areaPoints: string; baselinePoints: string | null;
  endX: number; endY: number;
  axisLabels: { x: number; label: string }[];
}
interface TrendChart {
  linePoints: string; areaPoints: string;
  gridYs: number[];
  benchY: number | null;
  dots: { x: number; y: number; score: number; labelY: number }[];
  axisLabels: { x: number; label: string; anchor: string }[];
  endX: number; endY: number;
}
interface RtoBar { x: number; y: number; height: number; color: string; opacity: number; month: string; days: number; }
interface RadarGeom {
  rings: string[]; axes: { x2: number; y2: number }[];
  labels: { x: number; y: number; anchor: string; text: string }[];
  nowPoints: string; prevPoints: string; nowDots: { x: number; y: number }[];
}

@Component({
  selector: 'app-employee-360',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './employee-360.component.html',
  styleUrls: ['./employee-360.component.scss'],
})
export class Employee360Component implements OnInit, OnChanges {
  /** Embedded mode (e.g. inside Team Composition): the host passes the
   *  employee instead of the route, and the header stops being sticky. */
  @Input() employeeId: string | null = null;
  @Input() embedded = false;

  readonly currentYear = new Date().getFullYear();

  /** Hover explanations for the KPI info icons. */
  readonly tips = {
    productFit: "Average fitment across this person's assigned products — how well their current skills match what each product needs.",
    sixBox: "Latest talent calibration placement on the 6-box grid (performance × potential), and its calibration status.",
    tScore: "Composite skill score: each rated skill earns points by its rating level, weighted by importance in the role's T-Map, compared against the role benchmark.",
    attrition: "Manager-assessed likelihood of leaving, plus whether the person's skills are fungible (coverable by others on the team). Visible to managers only.",
    aicUtilized: "Cumulative AI Console spend since launch (100 AIC credits = $1). Each person has a $30/month cap; the red flag totals spend beyond those monthly caps.",
    topModels: "Which AI models this person uses most through the AI Console, by share of their total consumed credits.",
    copilotDays: "How many days this person used GitHub Copilot in the last 30 days, with the two prior 30-day windows for trend.",
    agentsUsed: "Which of the four SDLC lifecycle agents (Designer, Planner, Developer, QE) this person actively uses.",
    certifications: "Certifications earned through Development goals (status Passed). Flags any expiring within the next 60 days.",
    productivity: "Sprint metrics from the Productivity module — the three metrics with the most history, last 7 data points each. The dashed line is the metric's baseline.",
    tsTrend: "T-Score over time, reconstructed by replaying each assessment save from the audit history through today's scoring rules. Dashed line = role benchmark.",
    competencies: "The person's current competency levels — shown as a radar and as a list (P0 lowest to P4 highest).",
    rto: "Days in office per month this year — green meets the 12-day target, amber 8–11, red under 8. The current month is still accruing.",
    rnr: "Rewards & Recognition from the IDC RnR process: awards won and the person's award mix.",
    succession: "Manager-maintained talent flags and succession plan: top/critical talent, successor status and readiness. Visible to managers only.",
    recommendations: "Rule-based suggestions from this profile's data: largest skill gaps vs the T-Map, expiring certifications, flat competencies, and stretch-target ideas.",
  };

  loading = true;
  errored = false;
  data: Employee360 | null = null;

  metricCharts: MetricChart[] = [];
  productivityLoading = true;

  rtoBars: RtoBar[] = [];
  rtoAvg: number | null = null;
  rtoLoading = true;

  radar: RadarGeom | null = null;
  tsTrend: TrendChart | null = null;

  /* T-Score donut geometry (r=28 → C=175.93) */
  donutCurrentDash = '0 175.93';
  donutProjectedDash = '0 175.93';

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private api: IskillApiService,
    private productivityService: ProductivityService,
    private teamManagementService: TeamManagementService,
    private zone: NgZone,
  ) {}

  ngOnInit(): void {
    const id = this.employeeId ?? this.route.snapshot.paramMap.get('employeeId');
    if (!id) { this.errored = true; this.loading = false; return; }
    this.load(id);
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Embedded hosts swap the selection without recreating the component.
    const change = changes['employeeId'];
    if (change && !change.firstChange && change.currentValue) {
      this.reset();
      this.load(change.currentValue);
    }
  }

  private reset(): void {
    this.loading = true;
    this.errored = false;
    this.data = null;
    this.metricCharts = [];
    this.productivityLoading = true;
    this.rtoBars = [];
    this.rtoAvg = null;
    this.rtoLoading = true;
    this.radar = null;
    this.tsTrend = null;
    this.donutCurrentDash = '0 175.93';
    this.donutProjectedDash = '0 175.93';
  }

  private load(employeeId: string): void {
    // zone.run: on the ?empId= deep link the trigger chain passes through the
    // Okta token promise, which drops out of the Angular zone — state set in
    // these callbacks then doesn't repaint until the next user interaction.
    // Re-entering the zone is a no-op when already inside it.
    this.api.get<Employee360>(`/employee-360/${employeeId}`).subscribe({
      next: (res) => this.zone.run(() => {
        this.loading = false;
        if (res.status === 'SUCCESS' && res.data) {
          this.data = res.data;
          this.buildDonut();
          this.buildRadar();
          this.buildTsTrend();
        } else {
          this.errored = true;
        }
      }),
      error: () => this.zone.run(() => { this.loading = false; this.errored = true; }),
    });

    this.productivityService.recordsForEmployee(employeeId, 8).subscribe({
      next: (records) => this.zone.run(() => { this.productivityLoading = false; this.buildMetricCharts(records || []); }),
      error: () => this.zone.run(() => { this.productivityLoading = false; }),
    });

    this.teamManagementService.getTeamCompositionDetails(employeeId).subscribe({
      next: (res: any) => this.zone.run(() => {
        this.rtoLoading = false;
        this.buildRtoBars(res?.data?.rtoDatesLast12Months || '');
      }),
      error: () => this.zone.run(() => { this.rtoLoading = false; }),
    });
  }

  /** Height % for the Copilot mini-bars (days out of 30, floor for visibility). */
  barPct(days: number): number {
    return Math.max(10, Math.min(100, (days / 30) * 100));
  }

  readonly agentNames = ['Designer', 'Planner', 'Developer', 'QE'];

  /** 1234567 → "1.2M", 82412 → "82.4K" — AIC credits are large numbers. */
  compact(n: number | null | undefined): string {
    if (n == null) return '—';
    if (Math.abs(n) >= 1_000_000) return (n / 1_000_000).toFixed(1) + 'M';
    if (Math.abs(n) >= 1_000) return (n / 1_000).toFixed(1) + 'K';
    return String(Math.round(n));
  }

  /* ── Header helpers ──────────────────────────────────────────────────── */

  /** Box label without its leading number — the mini-grid already shows
   *  the position, so "4 - Solid Performer, High Potential" → the name. */
  sixBoxName(): string {
    return (this.data?.sixBox?.boxLabel || '').replace(/^\s*\d+\s*[-–·:]\s*/, '');
  }

  /** RAW_ENUM_VALUES → readable text ("PENDING_CALIBRATION" → "Pending
   *  calibration", "LT_6_MONTHS" → "< 6 months"). */
  humanize(s: string | null | undefined): string {
    if (!s) return '';
    const t = s.replace(/_/g, ' ').toLowerCase()
      .replace(/^lt /, '< ').replace(/^gt /, '> ');
    return t.charAt(0).toUpperCase() + t.slice(1);
  }

  initials(): string {
    const n = this.data?.identity?.name || '';
    return n.split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0]).join('').toUpperCase();
  }

  /* ── T-Score donut ───────────────────────────────────────────────────── */

  private buildDonut(): void {
    const t = this.data?.tScore;
    if (!t || !t.benchmark) return;
    const C = 175.93;
    const cur = Math.min(t.current / t.benchmark, 1) * C;
    this.donutCurrentDash = `${cur.toFixed(1)} ${C}`;
    if (t.projected != null) {
      const proj = Math.min(t.projected / t.benchmark, 1) * C;
      this.donutProjectedDash = `${proj.toFixed(1)} ${C}`;
    }
  }

  /* ── T-Score trend (audit-log replay; 320×150 viewBox) ───────────────── */

  private buildTsTrend(): void {
    const t = this.data?.tScore;
    const pts = t?.trend || [];
    if (!t || pts.length < 2) return; // single point → keep the pending block

    // Wide, short canvas — rendered at max-width 680px so the type scale
    // matches the productivity tiles instead of stretching to the card.
    const W = 640, H = 170, padL = 18, padR = 96, padT = 26, padB = 28;
    const scores = pts.map(p => p.score);
    const lo = Math.min(...scores, t.benchmark);
    const hi = Math.max(...scores, t.benchmark);
    const span = Math.max(hi - lo, 1);
    const x = (i: number) => padL + (i / (pts.length - 1)) * (W - padL - padR);
    const y = (s: number) => padT + (1 - (s - lo) / span) * (H - padT - padB);

    const line = pts.map((p, i) => `${x(i).toFixed(1)},${y(p.score).toFixed(1)}`).join(' ');
    const area = `${padL},${H - padB} ${line} ${x(pts.length - 1).toFixed(1)},${H - padB}`;

    // At most 5 date labels, always including first and last; anchor the
    // edges inward so nothing clips.
    const step = Math.max(1, Math.ceil((pts.length - 1) / 4));
    const labelIdx = new Set<number>([0, pts.length - 1]);
    for (let i = step; i < pts.length - 1; i += step) labelIdx.add(i);
    const last = pts.length - 1;
    const axisLabels = [...labelIdx].sort((a, b) => a - b).map(i => ({
      x: x(i),
      label: new Date(pts[i].date + 'T00:00:00')
        .toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      anchor: i === 0 ? 'start' : i === last ? 'end' : 'middle',
    }));

    const plotTop = padT, plotBottom = H - padB;
    const gridYs = [1, 2, 3].map(k => plotTop + (k / 4) * (plotBottom - plotTop));

    this.tsTrend = {
      linePoints: line,
      areaPoints: area,
      gridYs,
      benchY: t.benchmark >= lo && t.benchmark <= hi ? y(t.benchmark) : null,
      dots: pts.map((p, i) => ({
        x: x(i), y: y(p.score), score: p.score,
        // value sits above the dot unless that would leave the canvas
        labelY: y(p.score) - 9 < 10 ? y(p.score) + 16 : y(p.score) - 9,
      })),
      axisLabels,
      endX: x(last),
      endY: y(pts[last].score),
    };
  }

  private static readonly MONTHS: Record<string, number> = {
    JAN: 0, FEB: 1, MAR: 2, APR: 3, MAY: 4, JUN: 5,
    JUL: 6, AUG: 7, SEP: 8, OCT: 9, NOV: 10, DEC: 11,
  };

  /** "AUG_2026" → 2026*12+7; unparseable ids collapse to 0 (string fallback). */
  private periodKey(periodId: string): number {
    const m = /^([A-Za-z]{3})[_-](\d{4})$/.exec(periodId || '');
    if (!m) return 0;
    const month = Employee360Component.MONTHS[m[1].toUpperCase()];
    return month === undefined ? 0 : Number(m[2]) * 12 + month;
  }

  /* ── Productivity per-metric line charts (260×110 viewBox) ───────────── */

  private buildMetricCharts(records: ProductivityRecord[]): void {
    const byMetric = new Map<string, ProductivityRecord[]>();
    for (const r of records) {
      if (r.actual == null) continue;
      const list = byMetric.get(r.metricId) || [];
      list.push(r);
      byMetric.set(r.metricId, list);
    }

    this.metricCharts = [...byMetric.entries()]
      .sort((a, b) => b[1].length - a[1].length)
      .slice(0, 3)
      .map(([metricId, rows]) => {
        // Chronological, not alphabetical: period ids are "MON_YYYY" strings,
        // and alphabetical order runs AUG < JUL < JUN — the chart rendered
        // time RIGHT-to-left. Parse to a sortable key; unknown formats fall
        // back to string order.
        rows.sort((a, b) => this.periodKey(a.periodId) - this.periodKey(b.periodId)
                            || a.periodId.localeCompare(b.periodId));
        const pts = rows.slice(-7);
        const values = pts.map(r => r.actual as number);
        const baselines = pts.map(r => r.baseline).filter((b): b is number => b != null);
        const min = Math.min(...values, ...(baselines.length ? baselines : values));
        const max = Math.max(...values, ...(baselines.length ? baselines : values));
        const span = max - min || 1;
        const x = (i: number) => pts.length === 1 ? 130 : 10 + (i * 240) / (pts.length - 1);
        const y = (v: number) => 90 - ((v - min) / span) * 62;

        const line = pts.map((r, i) => `${x(i).toFixed(1)},${y(r.actual as number).toFixed(1)}`).join(' ');
        const area = `${line} ${x(pts.length - 1).toFixed(1)},90 ${x(0).toFixed(1)},90`;
        const avgBaseline = baselines.length
          ? baselines.reduce((s, b) => s + b, 0) / baselines.length : null;

        // Month labels: first / middle / last periods.
        const label = (p: string) => {
          const m = p.match(/^(\d{4})-(\d{2})/);
          if (!m) return p;
          return new Date(+m[1], +m[2] - 1, 1).toLocaleString('default', { month: 'short' });
        };
        const axisIdx = pts.length > 2 ? [0, Math.floor(pts.length / 2), pts.length - 1] : pts.map((_, i) => i);
        const axisLabels = [...new Set(axisIdx)].map(i => ({ x: Math.max(2, x(i) - 8), label: label(pts[i].periodId) }));

        const last = pts[pts.length - 1];
        return {
          metricId,
          metricName: last.metricName,
          latest: String(last.actual),
          linePoints: line,
          areaPoints: area,
          baselinePoints: avgBaseline != null
            ? `10,${y(avgBaseline).toFixed(1)} 250,${y(avgBaseline).toFixed(1)}` : null,
          endX: x(pts.length - 1), endY: y(last.actual as number),
          axisLabels,
        };
      });
  }

  /* ── RTO bars — Jan → current month of this year (320×150 viewBox) ───── */

  private buildRtoBars(rtoDatesStr: string): void {
    const counts: Record<string, number> = {};
    for (const dateStr of rtoDatesStr ? rtoDatesStr.split(',') : []) {
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) continue;
      const key = `${d.getFullYear()}-${('0' + (d.getMonth() + 1)).slice(-2)}`;
      counts[key] = (counts[key] || 0) + 1;
    }
    const now = new Date();
    const bars: RtoBar[] = [];
    let sum = 0; let n = 0;
    for (let m = 0; m <= now.getMonth(); m++) {
      const key = `${now.getFullYear()}-${('0' + (m + 1)).slice(-2)}`;
      const days = counts[key] || 0;
      const monthLabel = new Date(now.getFullYear(), m, 1).toLocaleString('default', { month: 'short' });
      // y-scale: 12d → y30, 0d → y120 (7.5px per day, capped at 13+)
      const capped = Math.min(days, 13);
      const height = capped * 7.5;
      const color = days >= 12 ? 'var(--e360-met)' : days >= 8 ? 'var(--e360-pending)' : 'var(--e360-overdue)';
      const isCurrent = m === now.getMonth();
      bars.push({
        x: 30 + m * 24, y: 120 - height, height,
        color: days === 0 ? 'var(--e360-muted-2)' : color,
        opacity: isCurrent ? 0.45 : 1,
        month: monthLabel, days,
      });
      if (!isCurrent) { sum += days; n++; }
    }
    this.rtoBars = bars;
    this.rtoAvg = n > 0 ? Math.round((sum / n) * 10) / 10 : null;
  }

  /* ── Competency radar — N axes, now vs earliest-known (320×224) ──────── */

  private levelRank(level: string | null | undefined): number {
    if (!level) return 0;
    const m = level.match(/(\d+)/);
    return m ? +m[1] : 0;
  }

  private buildRadar(): void {
    const comps = this.data?.competencies?.current || [];
    if (comps.length < 3) { this.radar = null; return; }

    const cx = 160, cy = 112, R = 94, maxRank = 4;
    const n = comps.length;
    const angle = (i: number) => (Math.PI * 2 * i) / n - Math.PI / 2;
    const pt = (i: number, r: number) =>
      `${(cx + r * Math.cos(angle(i))).toFixed(1)},${(cy + r * Math.sin(angle(i))).toFixed(1)}`;
    const xy = (i: number, r: number) => ({
      x: +(cx + r * Math.cos(angle(i))).toFixed(1),
      y: +(cy + r * Math.sin(angle(i))).toFixed(1),
    });

    const rings = [0.25, 0.5, 0.75, 1].map(f =>
      comps.map((_, i) => pt(i, R * f)).join(' '));
    const axes = comps.map((_, i) => {
      const p = xy(i, R);
      return { x2: p.x, y2: p.y };
    });

    const labels = comps.map((c, i) => {
      const p = xy(i, R + 9);
      const cos = Math.cos(angle(i));
      const anchor = cos > 0.3 ? 'start' : cos < -0.3 ? 'end' : 'middle';
      return { x: p.x, y: p.y + 3, anchor, text: c.name };
    });

    // Earlier polygon: current level minus recorded movement (from-values).
    const fromByName = new Map(
      (this.data?.competencies?.movement || []).map(mv => [mv.name, mv.from]));
    const rOf = (rank: number) => (Math.min(rank, maxRank) / maxRank) * R;

    const nowPoints = comps.map((c, i) => pt(i, rOf(this.levelRank(c.level)))).join(' ');
    const prevPoints = comps.map((c, i) => {
      const from = fromByName.has(c.name) ? fromByName.get(c.name) : c.level;
      return pt(i, rOf(this.levelRank(from)));
    }).join(' ');
    const nowDots = comps.map((c, i) => xy(i, rOf(this.levelRank(c.level))));

    this.radar = { rings, axes, labels, nowPoints, prevPoints, nowDots };
  }

  /* ── Misc ────────────────────────────────────────────────────────────── */

  sixBoxCells(): boolean[] {
    // 2×3 mini grid, cells 1..6; the current box is highlighted.
    const box = this.data?.sixBox?.boxNumber;
    return [1, 2, 3, 4, 5, 6].map(i => i === box);
  }

  hasMovement(): boolean {
    return (this.data?.competencies?.movement?.length || 0) > 0;
  }

  openProfile(tab: string): void {
    if (!this.data) return;
    this.router.navigate(['/iskill'], {
      queryParams: { view: 'team', tab, employeeId: this.data.identity.employeeId },
    });
  }

  openRto(): void {
    // Same navigation-state contract as the team-dashboard RTO KPI
    // (team-management-row-two.onOpenNewClick): rto-details pre-selects the
    // passed employee in its reportee dropdown and scopes the data to them.
    if (!this.data) return;
    this.router.navigate(['/team-management/rto-details'], {
      state: {
        selectedDropdownValue: { employeeId: this.data.identity.employeeId },
      },
    });
  }

  navigateTo(route: string): void {
    const [path, query] = route.split('?');
    const queryParams: Record<string, string> = {};
    (query || '').split('&').filter(Boolean).forEach(kv => {
      const [k, v] = kv.split('=');
      queryParams[k] = decodeURIComponent(v || '');
    });
    this.router.navigate([path], { queryParams });
  }


}
